import { api, type ReliabilityOverview } from '../api';
import { barChart, bindCharts, sparkline, trendChart } from '../charts';
import { callout, field, hoursVal, input, opt, openDialog, select, todayISO, val, numVal } from '../dialog';
import { hoursInputAttrs } from '../hours';
import { errorCard, esc, fmtDate, fmtHours, html, loading, pill, tile } from '../ui';

/**
 * Reliability & trends.
 *
 * Condition monitoring: the point is to see a problem before it becomes a
 * defect. Everything here is a rate or a direction — consumption per flight
 * hour, defects per hundred hours, wear metals against their limits — because
 * a single reading says nothing and a slope says a great deal.
 */

const ELEMENT_NAME: Record<string, string> = {
  FE: 'Iron — cylinder walls, camshaft',
  AL: 'Aluminium — pistons',
  CU: 'Copper — bearings, bushings',
  CR: 'Chromium — rings, plating',
  SI: 'Silicon — ingested dust, sealant',
  NI: 'Nickel — valves',
  PB: 'Lead — fuel residue',
  MO: 'Molybdenum — rings',
  SN: 'Tin — bearings',
  AG: 'Silver — bearings',
};

const VERDICT_STATE: Record<string, 'OK' | 'DUE_SOON' | 'OVERDUE'> = {
  NORMAL: 'OK',
  MONITOR: 'DUE_SOON',
  ABNORMAL: 'OVERDUE',
};

function view(o: ReliabilityOverview): string {
  const alerts = o.oil.alerts;
  const worst = alerts[0] ?? o.oil.series.find((s) => s.points.length > 1);

  return html`
    <div class="page-h">
      <div>
        <h1>Reliability &amp; trends</h1>
        <p class="dim">
          Condition monitoring across the fleet — the point is to see a problem before it becomes a defect
        </p>
      </div>
      <button class="btn pri" data-log-oil>Log oil analysis</button>
    </div>

    <div class="tiles">
      ${tile({
        label: 'Trend alerts',
        value: alerts.length,
        sub: alerts.length ? `${alerts[0].registration} oil consumption` : 'nothing above threshold',
        tone: alerts.length ? 'caution' : 'ok',
      })}
      ${tile({
        label: 'Defect rate',
        value: o.defectRate.mean,
        sub: `per 100 FH · ${o.months}-month mean`,
      })}
      ${tile({
        label: 'Unscheduled removals',
        value: o.removals.length,
        sub: `last ${o.months} months`,
        tone: o.removals.length ? 'caution' : 'ok',
      })}
      ${tile({
        label: 'Hours flown',
        value: o.defectRate.totalHours,
        suffix: ' h',
        sub: `${o.defectRate.total} defects raised`,
      })}
    </div>

    <div class="cols">
      <div class="stack">
        ${worst && worst.points.length > 1
          ? html`
              <div class="card">
                <div class="card-h">
                  <div>
                    <h2>Oil consumption — ${esc(worst.registration)}</h2>
                    <p class="tiny dim">
                      Litres per 10 flight hours · ${esc(worst.typeModel)}
                    </p>
                  </div>
                  ${worst.alert
                    ? pill('DUE_SOON', 'Alert threshold reached')
                    : pill('OK', 'Within normal range')}
                </div>
                <div class="card-b">
                  ${trendChart(
                    worst.points.map((p) => ({ label: p.month.slice(5), value: p.perTenHours })),
                    o.oil.alertThreshold,
                    'L/10h',
                    `Oil consumption per ten flight hours for ${worst.registration} against the alert threshold`,
                  )}
                </div>
                <div class="card-f">
                  ${worst.changePct > 0
                    ? `Consumption is up ${worst.changePct}% over the period, now ${worst.latest.toFixed(2)} L per 10 flight hours against an alert threshold of ${o.oil.alertThreshold.toFixed(2)}.`
                    : `Consumption is stable at ${worst.latest.toFixed(2)} L per 10 flight hours.`}
                  A rising trend with iron or silicon in the oil sample points at cylinder wear, and is worth
                  a borescope well before TBO.
                </div>
              </div>
            `
          : ''}

        <div class="card">
          <div class="card-h">
            <h2>Fleet oil consumption</h2>
            <span class="lbl">litres per 10 flight hours</span>
          </div>
          <div class="sparks">
            ${o.oil.series
              .filter((s) => s.points.length > 1)
              .map(
                (s) => html`
                  <div class="spark${s.alert ? ' up' : ''}">
                    <span class="lbl">${esc(s.registration)}</span>
                    <span class="v">${s.latest.toFixed(2)}</span>
                    ${sparkline(s.points.map((p) => p.perTenHours), 120, 30)}
                    <span class="tiny dim">
                      ${s.changePct > 0 ? '▲' : s.changePct < 0 ? '▼' : '▬'} ${s.changePct}% over ${o.months} months
                    </span>
                  </div>
                `,
              )
              .join('')}
          </div>
        </div>

        <div class="card">
          <div class="card-h">
            <h2>Defects raised per 100 flight hours</h2>
            <span class="lbl">${o.months} months</span>
          </div>
          <div class="card-b">
            ${barChart(
              o.defectRate.series.map((d) => ({ label: d.month.slice(5), value: d.per100 })),
              ' per 100 FH',
              'Defects raised per hundred flight hours, by month',
            )}
          </div>
          <div class="card-f">
            Fleet mean ${o.defectRate.mean} per 100 flight hours over ${o.defectRate.totalHours} hours flown.
            A month above the mean is worth reading alongside the utilisation that produced it.
          </div>
        </div>
      </div>

      <div class="stack">
        ${o.analyses.length
          ? o.analyses
              .map(
                (a) => html`
                  <div class="card">
                    <div class="card-h">
                      <div>
                        <h2>Oil analysis — ${esc(a.registration)}</h2>
                        <p class="tiny dim">
                          ${esc(a.laboratory ?? 'laboratory not recorded')} · ${esc(fmtDate(a.sampledAt))}
                          ${a.hoursOnOil ? ` · ${fmtHours(a.hoursOnOil)} h on oil` : ''}
                        </p>
                      </div>
                      ${pill(VERDICT_STATE[a.overallVerdict ?? 'NORMAL'] ?? 'NA', a.overallVerdict ?? '—')}
                    </div>
                    <div class="scrollx">
                      <table class="tbl" style="min-width:0">
                        <thead><tr><th>Element</th><th class="n">ppm</th><th class="n">Limit</th><th></th></tr></thead>
                        <tbody>
                          ${a.elements
                            .map(
                              (e) => html`<tr>
                                <td class="st s-${e.state === 'EXCEEDED' ? 'exceed' : e.state === 'WATCH' ? 'caution' : 'ok'}">
                                  ${esc(e.element)}
                                  <span class="sub2">${esc(ELEMENT_NAME[e.element] ?? '')}</span>
                                </td>
                                <td class="n"><b>${e.ppm ?? '—'}</b></td>
                                <td class="n">${e.limitPpm ?? '—'}</td>
                                <td>
                                  ${e.state === 'EXCEEDED'
                                    ? pill('OVERDUE', 'Over limit')
                                    : e.state === 'WATCH'
                                      ? pill('DUE_SOON', 'Watch')
                                      : pill('OK', 'Normal')}
                                </td>
                              </tr>`,
                            )
                            .join('')}
                        </tbody>
                      </table>
                    </div>
                    ${a.note ? html`<div class="card-f">${esc(a.note)}</div>` : ''}
                  </div>
                `,
              )
              .join('')
          : html`
              <div class="card">
                <div class="card-h"><h2>Oil analysis</h2></div>
                <div class="card-b muted">
                  No spectrometric analysis on file. Logging one records each element against its own limit
                  and sets the overall verdict from the results.
                </div>
              </div>
            `}

        <div class="card">
          <div class="card-h">
            <h2>Unscheduled removals</h2>
            <span class="lbl">last ${o.months} months</span>
          </div>
          <div class="rows">
            ${o.removals.length
              ? o.removals
                  .map(
                    (r) => html`
                      <div class="row">
                        <div class="bd">
                          <div class="t">${esc(r.component)}</div>
                          <div class="m">
                            ${esc(r.registration)} · ${esc(r.partNumber)} · ${esc(fmtDate(r.removedAt))}
                            <br />${esc(r.reason ?? '')}
                          </div>
                        </div>
                      </div>
                    `,
                  )
                  .join('')
              : '<div class="row"><div class="bd"><div class="m">None recorded — every removal was at a scheduled limit.</div></div></div>'}
          </div>
        </div>
      </div>
    </div>
  `;
}

// ── dialogs ─────────────────────────────────────────────────────────────────

const DEFAULT_LIMITS: Record<string, number> = {
  FE: 60, AL: 20, CU: 30, CR: 10, SI: 25, NI: 5, PB: 4000, MO: 5, SN: 10, AG: 3,
};

function logOilAnalysis(o: ReliabilityOverview, reload: () => void): void {
  openDialog<Record<string, unknown>>({
    wide: true,
    title: 'Log oil analysis',
    sub: 'Spectrometric results. Each element is judged against its own limit, and the verdict follows from that',
    primary: 'Record analysis',
    body: () => html`
      <div class="frow3">
        ${field('Aircraft', select('aircraftId', o.fleet.map((a) => opt(a.id, a.registration)).join('')))}
        ${field('Sampled', input('sampledAt', todayISO(), 'type="date"'))}
        ${field('Laboratory', input('laboratory', '', 'maxlength="160"'))}
      </div>
      <div class="frow">
        ${field('Airframe hours at sample', input('atTsnHours', '', hoursInputAttrs()))}
        ${field('Hours on this oil', input('hoursOnOil', '', 'inputmode="decimal" style="text-align:right;font-family:var(--mono)"'))}
      </div>

      <div class="fset">
        <div class="lg">Wear metals — ppm</div>
        <div class="frow3">
          ${['FE', 'AL', 'CU'].map((e) => field(e, input(`el_${e}`, '', 'inputmode="decimal" style="text-align:right;font-family:var(--mono)"'), `limit ${DEFAULT_LIMITS[e]}`)).join('')}
        </div>
        <div class="frow3">
          ${['CR', 'SI', 'NI'].map((e) => field(e, input(`el_${e}`, '', 'inputmode="decimal" style="text-align:right;font-family:var(--mono)"'), `limit ${DEFAULT_LIMITS[e]}`)).join('')}
        </div>
      </div>

      ${field('Note', input('note', '', 'maxlength="4000"'))}
      ${callout('info', 'i', html`
        Leave an element blank if it was not reported. The overall verdict is set from the results — a sample
        with an element over limit is not "normal" because somebody ticked it.
      `)}
    `,
    collect: (root) => {
      const elements = Object.keys(DEFAULT_LIMITS)
        .map((e) => ({ element: e, ppm: numVal(root, `el_${e}`), limitPpm: DEFAULT_LIMITS[e] }))
        .filter((e): e is { element: string; ppm: number; limitPpm: number } => e.ppm !== undefined);

      if (!elements.length) return { error: 'Record at least one element reading.' };

      return {
        aircraftId: val(root, 'aircraftId'),
        sampledAt: val(root, 'sampledAt'),
        laboratory: val(root, 'laboratory') || undefined,
        atTsnHours: hoursVal(root, 'atTsnHours'),
        hoursOnOil: numVal(root, 'hoursOnOil'),
        note: val(root, 'note') || undefined,
        elements,
      };
    },
    submit: (payload) => api.reliability.logOilAnalysis(payload),
    done: (r) =>
      r.exceeded.length
        ? `${r.registration} — ${r.overallVerdict}: ${r.exceeded.join(', ')} over limit`
        : `${r.registration} — ${r.overallVerdict}`,
    onDone: reload,
  });
}

export async function renderReliability(host: HTMLElement): Promise<void> {
  host.innerHTML = loading('Loading reliability data');
  try {
    const o = await api.reliability.overview();
    host.innerHTML = view(o);
    bindCharts(host);
    host.querySelector('[data-log-oil]')?.addEventListener('click', () =>
      logOilAnalysis(o, () => void renderReliability(host)),
    );
  } catch (err) {
    host.innerHTML = errorCard(err instanceof Error ? err.message : 'Could not load reliability data.');
  }
}
